/**
 * The backend's `detail` for a missing or rejected bearer — an AUTHENTICATION answer,
 * not an authorization one. Several layers key off it (the SSE retry decision, the
 * public GPT page's "session expired" state, the toast suppression), so it lives here
 * rather than as a string literal repeated at each of them.
 */
export const NOT_AUTHENTICATED_DETAIL = 'Not authenticated' as const

/** The backend's `detail` for a bearer whose `exp` has passed. */
export const TOKEN_EXPIRED_DETAIL = 'Keycloak: Token has expired' as const

/** Shown instead of {@link TOKEN_EXPIRED_DETAIL} when the session could not be renewed. */
export const SESSION_EXPIRED_USER_MESSAGE =
  'Your session has expired. Reload the page to continue.'

/** Matches backend `fe_error_code` / SSE `code` for blocked GPT chat. */
export const FE_ERROR_GPT_NOT_READY = 'gpt_not_ready' as const

export const GPT_NOT_READY_USER_MESSAGE =
  "This GPT's knowledge files are not ready yet. Wait until processing finishes or fix failed files."

/** SSE `code` / API `fe_error_code` when the user lost access to the chat's GPT. */
export const FE_ERROR_GPT_ACCESS_REVOKED = 'gpt_access_revoked' as const

export const GPT_ACCESS_REVOKED_USER_MESSAGE =
  'You no longer have access to the assistant used in this chat.'

/**
 * RAG refused a SharePoint sync because it is still draining the file's previous
 * removal. Transient: the API parks the row in `retrying` and the reconcile cron
 * re-drives the sync, so this is a warning, never an error.
 */
export const FE_ERROR_RAG_SHAREPOINT_PENDING_REMOVAL =
  'rag_sharepoint_pending_removal' as const

/** Shown when the chat's GPT is missing or the user has no access to it. */
export const GPT_NO_ACCESS_USER_MESSAGE =
  'The creator has updated the settings for this GPT. You no longer have access to this custom GPT, but your conversation history will remain available. You can continue this chat as a standard conversation.'

/**
 * Shown when a chat stream ends without `message_complete` and without an `error` event.
 *
 * The backend cancels a stream server-side (its 900s streaming session timeout, a pod
 * restart, a dropped proxy connection) by simply closing the response body — the status
 * was already `200`, so the client sees a clean EOF that looks exactly like success.
 * It is a SOFT stop: the partial answer stays on screen, hence a warning, not an error.
 */
export const STREAM_INTERRUPTED_USER_MESSAGE =
  'The response was interrupted before it finished. The partial answer has been kept — send your message again to continue.'

/**
 * Shown once per stream when the provider's content filter blocked part of the exchange
 * (SSE `content_filter` with a category `filtered`). Detection alone is not shown: the
 * provider annotates prompts that way without blocking anything.
 */
export const CONTENT_FILTER_USER_MESSAGE =
  'Part of this conversation was blocked by the content safety filter, so the answer may be incomplete.'

/** Fallback for SSE `partial_context` when the backend sends no message of its own. */
export const PARTIAL_CONTEXT_USER_MESSAGE =
  'Some of this GPT’s knowledge files are still processing. The answer uses the files that are ready.'
